/**
 * Math helpers for editing block dimensions and split ratios in the
 * block configurator. The constants below match the schema bounds:
 *   - BlockDefinitionSchema.widthMm: 1..300 mm
 *   - SplitSchema.ratio: 0.05..0.95
 */
const DEFAULT_BLOCK_MIN_MM = 1
const DEFAULT_BLOCK_MAX_MM = 300
const DEFAULT_RATIO_MIN = 0.05
const DEFAULT_RATIO_MAX = 0.95

/**
 * Clamps a millimetre dimension into the schema range [min, max].
 * Default range is 1..300 mm (matching BlockDefinitionSchema).
 */
export function clampBlockDimensionMm(
  n: number,
  min: number = DEFAULT_BLOCK_MIN_MM,
  max: number = DEFAULT_BLOCK_MAX_MM
): number {
  if (n < min) return min
  if (n > max) return max
  return n
}

/**
 * Rounds `n` to the nearest multiple of `snapMm`. Returns `n` unchanged
 * when `snapMm <= 0` (no snapping). Use `snapMm: 1` for integer mm,
 * `snapMm: 5` for 5-mm grid.
 */
export function snapToMm(n: number, snapMm: number): number {
  if (!snapMm || snapMm <= 0) return n
  return Math.round(n / snapMm) * snapMm
}

/**
 * Computes the parent split's `ratio` such that the first child of the
 * split has a dimension of `desiredChildMm` along the split axis.
 *
 * Clamps the resulting ratio into [clampMin, clampMax] (default schema
 * range 0.05..0.95). If `parentTotalMm <= 0`, returns `clampMin` as a
 * defensive fallback.
 */
export function ratioForChildSize(
  desiredChildMm: number,
  parentTotalMm: number,
  clampMin: number = DEFAULT_RATIO_MIN,
  clampMax: number = DEFAULT_RATIO_MAX
): number {
  if (parentTotalMm <= 0) return clampMin
  const raw = desiredChildMm / parentTotalMm
  if (raw < clampMin) return clampMin
  if (raw > clampMax) return clampMax
  return raw
}

/**
 * Clamps a split ratio into [clampMin, clampMax]. Default range is
 * 0.05..0.95 (matching SplitSchema).
 */
export function clampSplitRatio(
  r: number,
  clampMin: number = DEFAULT_RATIO_MIN,
  clampMax: number = DEFAULT_RATIO_MAX
): number {
  if (r < clampMin) return clampMin
  if (r > clampMax) return clampMax
  return r
}
